import React, { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FaGoogle } from "react-icons/fa6";
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { customer_login, sendOtpLogin, verifyOtpLogin, messageClear } from '../store/reducers/authReducer';
import toast from 'react-hot-toast';
import { FadeLoader } from 'react-spinners';

const Login = () => {
  const navigate = useNavigate();
  const { loader, errorMessage, successMessage, userInfo } = useSelector(state => state.auth);
  const dispatch = useDispatch();

  const [state, setState] = useState({ email: '', password: '', phoneOrEmail: '', otp: '' });
  const [isOtpLogin, setIsOtpLogin] = useState(false);
  const [otpSent, setOtpSent] = useState(false);

  const inputHandle = (e) => {
    setState({ ...state, [e.target.name]: e.target.value });
  };

  const login = (e) => {
    e.preventDefault();

    if (isOtpLogin) {
      // ----------- OTP Login Flow -----------
      if (!otpSent) {
        // Determine if user entered email or phone
        const input = state.phoneOrEmail.trim();
        const isEmail = input.includes("@");
        const payload = isEmail
          ? { type: "email", email: input }
          : { type: "phone", phone: input };

        dispatch(sendOtpLogin(payload))
          .unwrap()
          .then(() => setOtpSent(true))
          .catch(() => setOtpSent(false));
      } else {
        // Verify OTP
        const input = state.phoneOrEmail.trim();
        const isEmail = input.includes("@");
        const payload = isEmail
          ? { type: "email", email: input, otp: state.otp }
          : { type: "phone", phone: input, otp: state.otp };

        dispatch(verifyOtpLogin(payload));
      }
    } else {
      // ----------- Manual Login Flow -----------
      dispatch(customer_login({ email: state.email, password: state.password }));
    }
  };

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      dispatch(messageClear());
    }
    if (errorMessage) {
      toast.error(errorMessage);
      dispatch(messageClear());
    }
    if (userInfo) {
      navigate('/');
    }
  }, [successMessage, errorMessage, userInfo, navigate, dispatch]);

  return (
    <div>
      {loader && (
        <div className='fixed inset-0 flex justify-center items-center bg-[#38303033] z-[999]'>
          <FadeLoader />
        </div>
      )}

      <Header />

      <div className='bg-slate-200 mt-4'>
        <div className='w-full justify-center items-center p-10'>
          <div className='grid grid-cols-2 sm:grid-cols-1 w-[60%] sm:w-[90%] mx-auto bg-white rounded-md'>
            <div className='px-8 py-8'>
              <h2 className='text-center w-full text-xl text-slate-600 font-bold'>
                {isOtpLogin ? 'Login With OTP' : 'Login'}
              </h2>

              <form onSubmit={login} className='text-slate-600 mt-4'>
                {!isOtpLogin && (
                  <>
                    <div className='flex flex-col gap-1 mb-2'>
                      <label htmlFor="email">Email</label>
                      <input
                        onChange={inputHandle}
                        value={state.email}
                        className='w-full px-3 py-2 border border-slate-200 outline-none focus:border-green-500 rounded-md'
                        type="email"
                        name="email"
                        id="email"
                        placeholder='Email'
                        required
                      />
                    </div>

                    <div className='flex flex-col gap-1 mb-2'>
                      <label htmlFor="password">Password</label>
                      <input
                        onChange={inputHandle}
                        value={state.password}
                        className='w-full px-3 py-2 border border-slate-200 outline-none focus:border-green-500 rounded-md'
                        type="password"
                        name="password"
                        id="password"
                        placeholder='Password'
                        required
                      />
                    </div>
                  </>
                )}

                {isOtpLogin && (
                  <>
                    <div className='flex flex-col gap-1 mb-2'>
                      <label htmlFor="phoneOrEmail">Phone or Email</label>
                      <input
                        onChange={inputHandle}
                        value={state.phoneOrEmail}
                        className='w-full px-3 py-2 border border-slate-200 outline-none focus:border-green-500 rounded-md'
                        type="text"
                        name="phoneOrEmail"
                        id="phoneOrEmail"
                        placeholder='Enter Phone or Email'
                        required
                      />
                    </div>

                    {otpSent && (
                      <div className='flex flex-col gap-1 mb-2'>
                        <label htmlFor="otp">Enter OTP</label>
                        <input
                          onChange={inputHandle}
                          value={state.otp}
                          className='w-full px-3 py-2 border border-slate-200 outline-none focus:border-green-500 rounded-md'
                          type="text"
                          name="otp"
                          id="otp"
                          placeholder='Enter OTP'
                          required
                        />
                      </div>
                    )}
                  </>
                )}

                <button className='px-8 w-full py-2 bg-[#059473] shadow-lg hover:shadow-green-500/40 text-white rounded-md mt-2'>
                  {isOtpLogin ? (otpSent ? 'Verify OTP' : 'Send OTP') : 'Login'}
                </button>
              </form>

              <div className='text-center mt-2'>
                <button
                  className='text-blue-500 underline text-sm'
                  onClick={() => {
                    setIsOtpLogin(!isOtpLogin);
                    setOtpSent(false);
                    setState({ email: '', password: '', phoneOrEmail: '', otp: '' });
                  }}>
                  {isOtpLogin ? 'Login with Password' : 'Login with OTP'}
                </button>
              </div>

              <div className='flex justify-center items-center py-2 mt-4'>
                <div className='h-[1px] bg-slate-300 w-[95%]'></div>
                <span className='px-3 text-slate-600'>Or</span>
                <div className='h-[1px] bg-slate-300 w-[95%]'></div>
              </div>

              <button className='px-8 w-full py-2 bg-red-500 shadow hover:shadow-red-500/50 text-white rounded-md flex justify-center items-center gap-2 mb-3'>
                <FaGoogle /> Login With Google
              </button>

              <div className='text-center text-slate-600 pt-1'>
                <p>Don't Have An Account? <Link className='text-blue-500' to='/register'> Register</Link></p>
              </div>

              {/* <a target='_blank' href="http://localhost:3001/login">
                <div className='px-8 w-full py-2 bg-[#02e3e0] shadow text-white rounded-md flex justify-center items-center gap-2 mb-3'>
                  Login As a Seller
                </div>
              </a> */}

              {/* <a target='_blank' href="http://localhost:3001/register">
                <div className='px-8 w-full py-2 bg-[#ad2cc4] shadow text-white rounded-md flex justify-center items-center gap-2 mb-3'>
                  Register As a Seller
                </div>
              </a> */}
            </div>

            <div className='w-full h-full py-4 pr-4'>
              <img src="/images/login.jpg" alt="" />
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Login;
